/**
 * Generation pipeline types and Zod schemas. Mirror the legacy V2 API spec;
 * consumed by both the studio core and the gallery surface.
 *
 * Zod schemas ship in this file so `packages/contract` is the single source
 * of truth for the wire contract (CLAUDE.md hard rule #2). Both sides of the
 * API boundary import from here — the backend for response validation, the
 * frontend via `@studio/api-client` for type-safe data handling.
 */

import { z } from 'zod';
import { GenerateTaskSchema } from './generate';

/**
 * v0 reduced task list. Legacy split match-style/match-scene and
 * merge-compose/merge-blend; we collapse those for now because the UX
 * differences blur — re-introduce as separate tasks once the scenes vs.
 * styles split actually shows up in user testing.
 *
 * Derived from `GenerateTaskSchema` so it can never drift from the
 * `GENERATE_TASKS` constant that is the single source of truth. Kept as
 * a separate alias (not re-exported as `GenerateTask`) for semantic
 * clarity in the generation pipeline context.
 */
export type GenerationTask = z.infer<typeof GenerateTaskSchema>;

export const GENERATION_STATUSES = [
  'PENDING',
  'IN_PROGRESS',
  'COMPLETED',
  'PARTIALLY_COMPLETED',
  'FAILED',
] as const;

export const GenerationStatusSchema = z.enum(GENERATION_STATUSES);
export type GenerationStatus = z.infer<typeof GenerationStatusSchema>;

export const IMAGE_STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED', 'DELETED'] as const;
export const ImageStatusSchema = z.enum(IMAGE_STATUSES);
export type ImageStatus = z.infer<typeof ImageStatusSchema>;

/** Internal credit-multiplier tier (1x / 2x / 3x). Distinct from `OutputSizeTier`. */
export type SizeTier = '1x' | '2x' | '3x';

export type EnhanceStyle =
  | 'auto'
  | 'studio'
  | 'professional'
  | 'creative'
  | 'natural'
  | 'bold'
  | 'elegant'
  | 'playful';

export type EnhanceAgentType = 'express' | 'art-director';

/** Open-ended provider identifier; `'flux-pro'` / `'flux-max'` are the known values. */
export type AIProvider = 'flux-pro' | 'flux-max' | (string & {});

/**
 * Runtime-validated shape of a single generation job. Both
 * `POST /api/generate` and `GET /api/generations` return this shape.
 *
 * `z.infer<>` derives the TypeScript type so the interface and schema
 * can never drift apart.
 */
export const GenerationJobSchema = z.object({
  id: z.string().min(1),
  task: GenerateTaskSchema,
  status: GenerationStatusSchema,
  /** Present once the job reaches the Gallery surface. */
  imageStatus: ImageStatusSchema.optional(),
  inputImageUrls: z.array(z.string()),
  originalPrompt: z.string(),
  enhancedPrompt: z.string().nullable(),
  outputImageUrl: z.string().nullable(),
  queueUrl: z.string().nullable(),
  creditsUsed: z.number(),
  /** Any string; 'flux-pro' / 'flux-max' / 'stub' are the common values. */
  aiProvider: z.string().min(1),
  seed: z.union([z.string(), z.number()]).nullable(),
  productUid: z.string().nullable(),
  parentTaskId: z.string().nullable(),
  workspaceId: z.string().min(1),
  /** Unix ms timestamp of when the job was created. */
  createdAt: z.number(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  error: z.string().nullable(),
});

export type GenerationJob = z.infer<typeof GenerationJobSchema>;

/**
 * `GET /api/generations` response shape.
 * Gallery iterates `body.items`; the array is empty (never null) on degrade.
 */
export const GenerationListResponseSchema = z.object({
  items: z.array(GenerationJobSchema),
});
export type GenerationListResponse = z.infer<typeof GenerationListResponseSchema>;
